/**
 * Reducer function to manage the state of the application.
 * @param state
 * @param action
 * @returns
 */
import { adminReducer } from './admin.reducer';
import { Site } from 'src/api/Api';
import { Action } from './action';
import { initialState, State, ProjectWithSites } from './state';
import { AnalysisStatus } from 'src/enum';
import { AdminAction } from './admin.action';

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_BIO_INTERACTIVE_MAP_FILTER':
      return { ...state, bioInteractiveMapFilter: action.payload };

    case 'SET_MAP_OPACITY':
      return {
        ...state,
        bioInteractiveMapFilter: {
          ...state.bioInteractiveMapFilter,
          mapOpacity: action.payload,
        },
      };

    case 'SET_PROJECT_LIST':
      return { ...state, projects: action.payload || [] };

    case 'SET_PROJECT_DETAILS':
      return {
        ...state,
        selectedProject: {
          ...state.selectedProject,
          projectDetails: action.payload,
        },
        projects: [
          ...state.projects.map((project: ProjectWithSites) => {
            if (project.project_id === action.payload?.project_id) {
              return { ...project, ...action.payload };
            }
            return project;
          }),
        ],
      };

    case 'SET_PROJECT_SITES':
      return {
        ...state,
        selectedProject: {
          ...state.selectedProject,
          projectDetails: state.selectedProject.projectDetails
            ? {
                ...state.selectedProject.projectDetails,
                sites: action.payload,
              }
            : undefined,
          hasAnyCarbonSiteAnalysis: action.payload?.some(
            (site: Site) =>
              site.carbon_analysis_enabled &&
              site.carbon_analysis_status === AnalysisStatus.COMPLETED
          ),
          hasAnyBiodiversitySiteAnalysis: action.payload?.some(
            (site: Site) =>
              site.bio_analysis_enabled &&
              site.bio_analysis_status === AnalysisStatus.COMPLETED
          ),
          hasAnyEnvironmentalSiteAnalysis: action.payload?.some(
            (site: Site) =>
              site.env_analysis_enabled &&
              site.env_analysis_status === AnalysisStatus.COMPLETED
          ),
        },
      };

    case 'SET_PROJECT_CARBON_ANALYSIS':
      return {
        ...state,
        selectedProject: {
          ...state.selectedProject,
          carbonAnalysis: action.payload,
        },
      };

    case 'SET_SITE_CARBON_ANALYSIS':
      return {
        ...state,
        selectedProject: {
          ...state.selectedProject,
          carbonSiteAnalysis: action.payload,
        },
      };

    case 'SET_PROJECT_BIODIVERSITY_ANALYSIS':
      return {
        ...state,
        selectedProject: {
          ...state.selectedProject,
          biodiversityProjectAnalysis: action.payload,
        },
      };

    case 'SET_SITE_BIODIVERSITY_ANALYSIS':
      return {
        ...state,
        selectedProject: {
          ...state.selectedProject,
          biodiversitySiteAnalysis: action.payload,
        },
      };

    case 'SET_SELECTED_SITE':
      return { ...state, selectedSite: action.payload };

    case 'SET_PROJECT_ENVIRONMENTAL_ANALYSIS':
      return {
        ...state,
        selectedProject: {
          ...state.selectedProject,
          environmentalProjectAnalysis: action.payload,
        },
      };

    case 'SET_SITE_ENVIRONMENTAL_ANALYSIS':
      return {
        ...state,
        selectedProject: {
          ...state.selectedProject,
          environmentalSiteAnalysis: action.payload,
        },
      };

    case 'SET_USER_DETAILS':
      return { ...state, user: action.payload };

    case 'SET_PROJECT_MAP_DATA':
      return {
        ...state,
        selectedProject: {
          ...state.selectedProject,
          bioMapData: action.payload,
        },
      };

    case 'CLEAR_USER_DETAILS':
      return { ...state, user: undefined };

    case 'LOGOUT':
      return { ...initialState };

    case 'SET_ADMIN_SELECTED_LOCATION':
    case 'SET_ADMIN_SELECTED_DEPLOYMENT':
    case 'ADMIN_SET_USERS':
    case 'ADMIN_DELETE_USER':
    case 'ADMIN_SET_USERS_LOADING':
    case 'ADMIN_SET_USERS_ERROR':
    case 'ADMIN_SET_USERS_PAGINATION':
    case 'ADMIN_SET_USERS_SORTING':
    case 'ADMIN_SET_PROJECTS':
    case 'ADMIN_SET_DEVICES':
    case 'ADMIN_SET_DEPLOYMENT_LIST':
    case 'ADMIN_SET_PROJECT_MEDIA':
    case 'ADMIN_ADD_PROJECT_MEDIA':
    case 'ADMIN_UPDATE_PROJECT_MEDIA':
    case 'ADMIN_DELETE_PROJECT_MEDIA':
      return {
        ...state,
        admin: adminReducer(state.admin, action as AdminAction),
      };

    default:
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      throw new Error(`Unhandled action type: ${(action as any).type}`);
  }
};